import { owlReportMetricsWeb } from '@RootCommon/utils/owl'
import { LastHeartBeatInfo, SystemResourceInfo } from './type'
import { getHornConfig } from '@RootCommon/utils/horn'
import { getClientType } from '@RootCommon/utils'
import { loganLog } from '@RootCommon/utils/logan'
import { leopardReport } from '../../leopardReport'

const OFFLINE_REASON_CODE_MAP = {
  bad_network: 10001,
  weak_network: 10002,
  system_busy: 10003,
  om_closed: 10004,
  system_slept: 10005,
  other: 20000,
}

export const OFFLINE_GAP_TIME = 5 * 60 * 1000

async function getOfflineReason (lastHeartBeatInfo: LastHeartBeatInfo) {
  const windowsStore = window.electronAPI?.store
  if (windowsStore) {
    // 1. 系统休眠
    try {
      const systemMonitorData = await windowsStore.get('systemMonitor')
      const { awake_time, sleep_time } = systemMonitorData || {}
      if (sleep_time && awake_time - sleep_time > OFFLINE_GAP_TIME) {
        // 如果命中，则清空休眠时间记录，防止系统休眠和OM关闭场景同时存在时，无法确认应该上报哪种情况
        windowsStore.set('systemMonitor.sleep_time', null)
        return OFFLINE_REASON_CODE_MAP.system_slept
      }
    } catch (error) {
      console.error('5min无心跳原因上报 - 获取系统休眠时间失败', error)
      loganLog({ msg: '5min无心跳原因上报 - 获取系统休眠时间失败', error })
    }

    // 2. OM 关闭
    try {
      const appMonitorData = await windowsStore.get('appMonitor')
      const { app_open_time, app_close_time } = appMonitorData || {}
      if (app_close_time && app_open_time - app_close_time > OFFLINE_GAP_TIME) {
        // 如果命中，则清空休眠时间记录，原因同上
        windowsStore.set('appMonitor.app_close_time', null)
        return OFFLINE_REASON_CODE_MAP.om_closed
      }
    } catch (error) {
      console.error('5min无心跳原因上报 - 获取OM关闭时间失败', error)
      loganLog({ msg: '5min无心跳原因上报 - 获取OM关闭时间失败', error })
    }
  }

  // 3. 网络异常
  if (lastHeartBeatInfo.isFailed) {
    return lastHeartBeatInfo.isNetworkConnected ? OFFLINE_REASON_CODE_MAP.weak_network : OFFLINE_REASON_CODE_MAP.bad_network
  }

  // 4. 系统资源
  const systemResourceFn = window.electronAPI?.getSystemCpuMemoryInfo
  if (systemResourceFn) {
    try {
      const { appCpuUsage, appMemoryUsage, appMemoryUsageRate, systemCpuUsage, systemMemoryUsageRate, systemTotalMemory } = (await systemResourceFn()) as SystemResourceInfo
      // 猎豹上报以上系统资源
      leopardReport('custom', 'offline_system_resource', {
        systemResource: JSON.stringify({
          appCpuUsage,
          appMemoryUsage,
          appMemoryUsageRate,
          systemCpuUsage,
          systemMemoryUsageRate,
          systemTotalMemory,
        }),
      })
      const config = await getHornConfig()
      const systemSourceConfig = config.offlineSystemResourceConfig
      // 满足任一条件即认为系统资源不足
      const isSystemResourceBusy = systemSourceConfig.some(item => {
        if (item.sourceType === 'cpu') {
          if (appCpuUsage[item.calcType] > item.threshold) {
            return true
          }
        } else if (item.sourceType === 'memory') {
          if (appMemoryUsageRate[item.calcType] > item.threshold) {
            return true
          }
        }
        return false
      })
      if (isSystemResourceBusy) {
        return OFFLINE_REASON_CODE_MAP.system_busy
      }
    } catch (error) {
      console.error('5min无心跳原因上报 - 获取系统资源失败', error)
      loganLog({ msg: '5min无心跳原因上报 - 获取系统资源失败', error })
    }
  }
  // 5. 其他
  return OFFLINE_REASON_CODE_MAP.other
}

export async function reportOfflineReason ({ lastHeartBeatInfo }: { lastHeartBeatInfo: LastHeartBeatInfo }) {
  const reasonCode = await getOfflineReason(lastHeartBeatInfo)
  // raptor上报 - 无心跳置休原因
  owlReportMetricsWeb({ offline_reason: 1 }, { reasonCode })
  // 猎豹上报
  leopardReport('custom', 'offline_reason', {
    offlineReasonInfo: JSON.stringify({
      reasonCode,
      lastHeartBeatInfo,
      clientType: getClientType(),
    }),
  })
}

/**
 * 心跳间隔上报
 */
export function reportHeartBeatGutter (gutter: number) {
  let gutterLevel = 0
  if (gutter < 30 * 1000) {
    // 30s内
    gutterLevel = 1
  } else if (gutter < 60 * 1000) {
    // 1min内
    gutterLevel = 2
  } else if (gutter < 180 * 1000) {
    // 5min内
    gutterLevel = 3
  } else {
    // 超出5min
    gutterLevel = 4
  }
  owlReportMetricsWeb({ heart_beat_gutter: 1 }, { gutterLevel })
}
